import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Award, GraduationCap, Stethoscope, ShieldCheck } from 'lucide-react';

interface ClinicalStandard {
  category: string;
  title: string;
  description: string;
  highlight: string;
}

const clinicalStandards: ClinicalStandard[] = [
  {
    category: "Pre-Operative Care",
    title: "Thorough Pre-Surgical Evaluation & Counseling",
    description: "Every patient undergoes comprehensive clinical and diagnostic assessment. Surgical risks, conservative non-operative alternatives, and tailored treatment pathways are explained in detail to help families make informed medical decisions.",
    highlight: "Evidence-Based Decision Making"
  },
  {
    category: "Surgical Standards",
    title: "Advanced Microsurgical & Endoscopic Planning",
    description: "Utilizing modern surgical microscopes, neuro-navigation tracking, and minimally invasive keyhole techniques designed to protect delicate neurological structures and preserve functional pathways.",
    highlight: "Precision Microsurgery"
  },
  {
    category: "Multidisciplinary Care",
    title: "Integrated Neuro-Oncology & Spine Collaboration",
    description: "Complex cranial and spinal cases are reviewed in collaboration with critical care intensivists, neuro-radiologists, and rehabilitation specialists to maintain high standards of patient safety throughout hospital admission.",
    highlight: "Comprehensive Team Approach"
  },
  {
    category: "Post-Operative Recovery",
    title: "Structured Rehabilitation & Patient-Centric Follow-Up",
    description: "Post-surgical recovery focuses on monitored recovery protocols, neuro-rehabilitation, and structured follow-up consultations to assist patients through long-term physical recovery and functional improvement.",
    highlight: "Continuity of Care"
  },
  {
    category: "Patient Communication",
    title: "Transparent Clinical Guidance & Ethical Practice",
    description: "Prioritizing open, honest discussions regarding diagnosis, prognosis, and realistic recovery timelines. Patient autonomy, dignity, and compassionate care guide every consultation.",
    highlight: "Patient-First Ethics"
  }
];

const Testimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % clinicalStandards.length);
    }, 12000);

    return () => clearInterval(interval);
  }, []);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + clinicalStandards.length) % clinicalStandards.length);
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % clinicalStandards.length);
  };

  return (
    <motion.section
      id="testimonials"
      className="bg-[#e8f8fa] py-16 px-4 h-[100%] sm:px-6 lg:px-8 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="inline-block text-xs uppercase tracking-wider font-semibold text-blue-800 bg-blue-100 px-3 py-1 rounded-full mb-3">
            Clinical Standards & Patient Guidance
          </span>
          <h2 className="text-3xl md:text-5xl text-gray-900 font-semibold text-center mb-3">
            Neurosurgical Care Philosophy
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg">
            Structured clinical protocols and ethical neurosurgical care for brain, spine, and nerve disorders
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 relative">
          <div className="col-span-1 shadow-2xl rounded-full lg:hidden">
            <img
              src="/images/testimonials.png"
              alt="Dr. Gangesh Gunjan"
              className="w-full h-full object-cover filter brightness-30"
            />
          </div>
          <div className="lg:col-span-3 relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-2xl shadow-xl p-6 md:p-10 h-full border border-blue-50"
              >
                <div className="flex flex-col h-full justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-3 bg-blue-50 text-blue-700 rounded-xl">
                          <ShieldCheck className="w-6 h-6" />
                        </div>
                        <div>
                          <span className="text-xs uppercase tracking-wider font-bold text-blue-700 block">
                            {clinicalStandards[currentIndex].category}
                          </span>
                          <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                            {clinicalStandards[currentIndex].title}
                          </h3>
                        </div>
                      </div>
                      <span className="hidden sm:inline-block text-xs font-semibold px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
                        {clinicalStandards[currentIndex].highlight}
                      </span>
                    </div>
                    <p className="text-gray-700 text-base md:text-lg leading-relaxed mt-4 mb-6">
                      {clinicalStandards[currentIndex].description}
                    </p>
                  </div>

                  <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                    <button
                      onClick={handlePrev}
                      aria-label="Previous standard"
                      className="bg-white rounded-full p-2.5 shadow-md hover:bg-gray-100 text-gray-600 transition-colors duration-200 border border-gray-200"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div className="flex space-x-2">
                      {clinicalStandards.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentIndex(index)}
                          aria-label={`Go to slide ${index + 1}`}
                          className={`w-3 h-3 rounded-full transition-all duration-300 ${
                            index === currentIndex ? 'bg-blue-600 w-6' : 'bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <button
                      onClick={handleNext}
                      aria-label="Next standard"
                      className="bg-white rounded-full p-2.5 shadow-md hover:bg-gray-100 text-gray-600 transition-colors duration-200 border border-gray-200"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Verified Credentials Banner */}
        <motion.div
          className="w-[90vw] md:w-[70vw] lg:w-[60vw] mx-auto mt-20 py-4 px-2"
        >
          <motion.div
            className="flex flex-col md:flex-row justify-around items-center space-y-6 md:space-y-0 bg-white/90 backdrop-blur-md py-6 px-8 rounded-2xl shadow-lg border border-blue-100"
            whileHover={{ scale: 1.01 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <motion.div
              className="text-center"
              whileHover={{ y: -3 }}
            >
              <div className="flex items-center justify-center mb-1 text-blue-900">
                <Stethoscope className="w-7 h-7 mr-2" />
                <span className="text-3xl md:text-4xl font-bold">12+</span>
              </div>
              <p className="text-sm md:text-base font-medium text-gray-600">Years Clinical Experience</p>
            </motion.div>

            <div className="hidden md:block w-px h-12 bg-gray-200" />

            <motion.div
              className="text-center"
              whileHover={{ y: -3 }}
            >
              <div className="flex items-center justify-center mb-1 text-blue-900">
                <GraduationCap className="w-7 h-7 mr-2" />
                <span className="text-2xl md:text-3xl font-bold">M.Ch</span>
              </div>
              <p className="text-sm md:text-base font-medium text-gray-600">Neurosurgery Qualification</p>
            </motion.div>

            <div className="hidden md:block w-px h-12 bg-gray-200" />

            <motion.div
              className="text-center"
              whileHover={{ y: -3 }}
            >
              <div className="flex items-center justify-center mb-1 text-amber-600">
                <Award className="w-7 h-7 mr-2" />
                <span className="text-2xl md:text-3xl font-bold">Gold Medalist</span>
              </div>
              <p className="text-sm md:text-base font-medium text-gray-600">Academic Distinction</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Testimonials;